import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, idr } from "../../lib/api";
import { toast } from "sonner";
import { PieChart, TrendingUp, AlertTriangle, Wallet, Percent, Package } from "lucide-react";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, bulanIniISO } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";

// Awal bulan berjalan (WIB) — default periode laporan bulan ini, bukan "hari ini".
const firstDayOfMonth = () => `${bulanIniISO()}-01`;
const missingCost = row => row.biaya_belum_dicatat > 0 ||
  (row.biaya_belum_dicatat == null && row.modal_lengkap === false);

export default function FinanceLabaKategori() {
  const {can} = useAuth();
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(hariIniISO());
  const [cabang, setCabang] = useState("");
  const [groupBy, setGroupBy] = useState("kategori"), [deviceCategory, setDeviceCategory] = useState("");
  const groupLabel = groupBy === "merek" ? "Merek Produk" : groupBy === "perangkat" ? "Kategori Perangkat" : "Kategori";
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false); // backend belum ter-restart / belum tersedia
  const [errMsg, setErrMsg] = useState("");

  // Nomor urut request — filter yang diklik beruntun tidak boleh membiarkan
  // respons lama menimpa hasil filter aktif (pola yang sama dengan AttendanceReview.jsx).
  const reqSeq = useRef(0);
  const load = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setNotFound(false);
    setErrMsg("");
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (cabang) p.set("cabang", cabang);
    p.set("group_by", groupBy);
    if (deviceCategory) p.set("device_category", deviceCategory);
    api.get(`/finance/laporan/kategori?${p}`)
      .then(r => { if (seq === reqSeq.current) setData(r.data); })
      .catch(e => {
        if (seq !== reqSeq.current) return;
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          setErrMsg(e?.response?.data?.detail || "Gagal memuat laporan laba per kategori");
          toast.error(e?.response?.data?.detail || "Gagal memuat laporan laba per kategori");
        }
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dateFrom, dateTo, cabang, groupBy, deviceCategory]);

  // Backend (`hitung_laporan_kategori` di reports.py) mengembalikan kunci `per_kategori`.
  const items = data?.per_kategori || [];
  const total = data?.total || { omzet: 0, hpp: 0, laba: 0, qty: 0 };
  const totalLabaAbs = items.filter(it => it.modal_lengkap !== false).reduce((a, it) => a + Math.abs(it.laba || 0), 0);
  const incomplete = total.modal_lengkap === false || items.some(it => it.modal_lengkap === false);
  const hasMissingCost = items.some(missingCost);
  const canOpenCosts = can("inventory_produk", "view") && can("sales", "view");

  // Hasil AGREGASI backend dikelompokkan per kategori (daftar kategori kecil
  // & tetap) — bukan potongan N-teratas, aman disortir tanpa BatasDaftarNotice.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kategori: (it) => it.label || it.kategori || "",
    qty: (it) => it.qty || 0,
    omzet: (it) => it.omzet || 0,
    hpp: (it) => it.hpp || 0,
    laba: (it) => it.laba || 0,
    marjin: (it) => missingCost(it) ? null : (it.omzet ? (it.laba / it.omzet) * 100 : 0),
  });

  return (
    <div className="p-4 md:p-8 max-w-[1400px] space-y-6" data-testid="finance-laba-kategori">
      <header>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Laporan &amp; Kontrol</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Laba per {groupLabel}</h1>
        <p className="text-sm text-slate-600 mt-1">
          Bandingkan omzet, HPP, dan laba menurut kategori atau merek produk. Pilih Android untuk melihat penjualan Samsung, Xiaomi, OPPO, dan merek lainnya.
        </p>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
        <label className="block text-xs font-medium text-slate-600">Kelompok laporan<select aria-label="Kelompok laporan" data-testid="laba-kat-group" className="mt-1 block h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" value={groupBy} onChange={e => setGroupBy(e.target.value)}><option value="kategori">Kategori keuangan</option><option value="perangkat">Kategori perangkat</option><option value="merek">Merek produk</option></select></label>
        <label className="block text-xs font-medium text-slate-600">Kategori perangkat<select aria-label="Kategori perangkat" data-testid="laba-kat-device" className="mt-1 block h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" value={deviceCategory} onChange={e => setDeviceCategory(e.target.value)}><option value="">Semua perangkat & layanan</option>{(data?.device_categories || ["iPhone", "Android", "Mac", "iPad", "AirPods", "Apple Watch", "Aksesoris"]).map(c => <option key={c}>{c}</option>)}</select></label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom}
            data-testid="laba-kat-from"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo}
            data-testid="laba-kat-to"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Cabang</span>
          <select value={cabang} onChange={e => setCabang(e.target.value)}
            data-testid="laba-kat-cabang"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Cabang"}</option>)}
          </select>
        </label>
      </div>
      {(groupBy !== "kategori" || deviceCategory) && <p className="text-xs text-slate-500">Merek produk berbeda dari brand usaha/cabang. Transaksi baru menyimpan merek saat penjualan; transaksi lama yang belum menyimpannya memakai master produk saat ini. Merek yang kosong ditampilkan sebagai “Merek belum diisi”. Omzet mengikuti nilai barang sebelum diskon / retur, konsisten dengan laporan kategori.</p>}

      {notFound && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3" data-testid="laba-kat-notfound">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Laporan belum tersedia</p>
            <p className="text-xs text-amber-800 mt-1">
              Endpoint laporan laba per kategori belum aktif di server. Coba lagi setelah backend selesai deploy.
            </p>
          </div>
        </div>
      )}

      {!notFound && (
        <LoadingState loading={loading} error={errMsg || null} onRetry={load} varian="kartu" jumlahKartu={4} data-testid="loading-laba-kategori">
        <>
          {incomplete && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="laba-kat-pending-cost">
            <strong>{hasMissingCost ? "HPP belum lengkap — laba belum final" : "Proses belum selesai — laba sementara"}</strong>
            <p className="mt-1">{hasMissingCost ? "Modal default adalah acuan. HPP layanan masuk setelah biaya aktual provider dicatat. Biaya yang belum dibukukan tidak dianggap Rp0 atau keuntungan 100%." : "HPP yang sudah dibukukan tetap dihitung. Angka laba dan margin masih sementara sampai layanan atau penyerahan pelengkap selesai."}</p>
            {items.some(it => it.kategori === "aktivasi_imei" && it.modal_lengkap === false) && canOpenCosts && <Link className="mt-2 inline-block font-semibold underline" to="/inventory?tab=pelengkap&section=orders">{items.some(it => it.kategori === "aktivasi_imei" && missingCost(it)) ? "Catat HPP Aktivasi IMEI" : "Lihat proses Aktivasi IMEI"}</Link>}
          </div>}
          {/* Ringkasan total */}
          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <Kpi icon={TrendingUp} label="Omzet" value={idr(total.omzet)} tint="slate" />
            <Kpi icon={Wallet} label="HPP tercatat" value={idr(total.hpp)} tint="amber" />
            <Kpi icon={Percent} label={incomplete ? "Laba sementara" : "Laba"} value={hasMissingCost ? "Belum final" : idr(total.laba)} tint="emerald" />
            <Kpi icon={Package} label="Qty Terjual" value={total.qty ?? 0} tint="sky" />
          </div>

          {/* Bar proporsi laba per kategori — CSS murni */}
          {items.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="font-display font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <PieChart className="w-4 h-4 text-emerald-600" /> Proporsi Laba per {groupLabel}
              </h3>
              <p className="text-xs text-slate-500 mb-4">Panjang batang = share laba kategori yang modal dan prosesnya sudah lengkap.</p>
              <div className="space-y-3">
                {items.map(it => {
                  const share = it.modal_lengkap !== false && totalLabaAbs > 0 ? Math.abs(it.laba || 0) / totalLabaAbs * 100 : 0;
                  const negatif = (it.laba || 0) < 0;
                  return (
                    <div key={it.kategori} data-testid={`laba-kat-bar-${it.kategori}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700">{it.label || it.kategori}</span>
                        <span className={`font-mono font-semibold ${negatif ? "text-rose-600" : "text-emerald-700"}`}>{it.modal_lengkap === false ? "Belum final" : idr(it.laba)}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${negatif ? "bg-rose-500" : "bg-emerald-600"}`}
                          style={{ width: `${it.modal_lengkap === false ? 0 : Math.max(share, it.laba ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabel per kategori */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-slate-900">Rincian per {groupLabel}</h3>
              <p className="text-xs text-slate-500 mt-1">Modal pelengkap gratis masuk ke HPP HP penerima. Aksesoris berbayar dihitung terpisah; jumlah pelengkap tidak menambah jumlah HP terjual.</p>
            </div>
            <table className="eh-mobile-records eh-metric-records w-full text-sm" data-testid="laba-kat-tabel">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <SortTh label={groupLabel} sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="laba-kat-sort-kategori" />
                  <SortTh label="Qty" sortKeyName="qty" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-qty" />
                  <SortTh label="Omzet" sortKeyName="omzet" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-omzet" />
                  <SortTh label="HPP" sortKeyName="hpp" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-hpp" />
                  <SortTh label="Laba" sortKeyName="laba" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-laba" />
                  <SortTh label="Marjin" sortKeyName="marjin" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-marjin" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-sm text-slate-400" data-testid="laba-kat-empty">
                      Tidak ada data pada rentang &amp; cabang ini.
                    </td>
                  </tr>
                )}
                {sortedItems.map(it => {
                  const marjin = it.omzet ? (it.laba / it.omzet) * 100 : 0;
                  return (
                    <tr key={it.kategori} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`laba-kat-row-${it.kategori}`}>
                      <td className="eh-record-title py-3 px-4 font-medium text-slate-900">{it.label || it.kategori}{it.modal_lengkap === false && <p className="mt-1 max-w-xs text-xs font-normal text-amber-700">{it.keterangan || "Modal belum lengkap"}</p>}</td>
                      <td data-label="Qty" className="py-3 px-4 text-right font-mono">{it.qty ?? 0}</td>
                      <td data-label="Omzet" className="py-3 px-4 text-right font-mono">{idr(it.omzet)}</td>
                      <td data-label="HPP" className="py-3 px-4 text-right font-mono text-slate-600">{it.biaya_belum_dicatat > 0 && !it.hpp ? "Belum dicatat" : idr(it.hpp)}{it.qty_pelengkap > 0 && <p className="text-xs font-sans mt-1" data-testid={`laba-kat-pelengkap-${it.kategori}`}>Termasuk pelengkap {idr(it.hpp_pelengkap)} · {it.qty_pelengkap} pcs</p>}</td>
                      <td data-label="Laba" className={`py-3 px-4 text-right font-mono font-semibold ${(it.laba || 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{missingCost(it) ? "Belum final" : idr(it.laba)}{!missingCost(it) && it.proses_belum_selesai && <p className="text-xs font-normal text-amber-700">Sementara</p>}</td>
                      <td data-label="Marjin" className="py-3 px-4 text-right font-mono text-slate-600">{missingCost(it) ? "—" : `${marjin.toFixed(1)}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                    <td className="eh-record-title py-3 px-4 text-slate-900">Total</td>
                    <td data-label="Qty" className="py-3 px-4 text-right font-mono">{total.qty ?? 0}</td>
                    <td data-label="Omzet" className="py-3 px-4 text-right font-mono">{idr(total.omzet)}</td>
                    <td data-label="HPP" className="py-3 px-4 text-right font-mono text-slate-600">{idr(total.hpp)}</td>
                    <td data-label="Laba" className={`py-3 px-4 text-right font-mono ${(total.laba || 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{hasMissingCost ? "Belum final" : idr(total.laba)}</td>
                    <td data-label="Marjin" className="py-3 px-4 text-right font-mono text-slate-600">{hasMissingCost ? "—" : `${total.omzet ? ((total.laba / total.omzet) * 100).toFixed(1) : "0.0"}%`}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
        </LoadingState>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-lg md:text-xl break-words font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
